export interface IRect {
  width: number;
  height: number;
  left: number;
  top: number;
}

export interface IPoint {
  x: number;
  y: number;
}

export function isRectBetweenPoints(rect: IRect, startPoint: IPoint, endPoint: IPoint) {
  const inputRight = rect.left + rect.width;
  const inputBottom = rect.top + rect.height;

  const minX = Math.min(startPoint.x, endPoint.x);
  const minY = Math.min(startPoint.y, endPoint.y);

  const maxX = Math.max(startPoint.x, endPoint.x);
  const maxY = Math.max(startPoint.y, endPoint.y);

  return rect.left >= minX && rect.top >= minY && inputRight <= maxX && inputBottom <= maxY;
}

/**
 * Returns boundary rectangle
 */
export function getBoundRect(rects: IRect[] | null): IRect | null {
  if (rects == null || rects.length === 0) {
    return null;
  }

  const horizontal = getBounds(rects, (item) => item.left, item => item.width);
  const vertical = getBounds(rects, (item) => item.top, item => item.height);

  return {
    left: horizontal.position,
    width: horizontal.size,
    top: vertical.position,
    height: vertical.size,
  };
}

export function convertToRelativeCoordinates(point: React.MouseEvent | IPoint, referenceElement: HTMLElement): IPoint {
  const position = {
    x: (point as React.MouseEvent).pageX ?? (point as IPoint).x,
    y: (point as React.MouseEvent).pageY ?? (point as IPoint).y,
  };

  const offset = {
    left: referenceElement.offsetLeft,
    top: referenceElement.offsetTop
  };

  let reference = referenceElement.offsetParent as HTMLElement;

  while (reference) {
    offset.left += reference.offsetLeft;
    offset.top += reference.offsetTop;
    reference = reference.offsetParent as HTMLElement;
  }

  return {
    x: position.x - offset.left + referenceElement.scrollLeft,
    y: position.y - offset.top + referenceElement.scrollTop,
  };
}

export function isPointsEqual(point1: IPoint, point2: IPoint) {
  return point1.x === point2.x && point1.y === point2.y;
}

/**
 * Calculates new position for every item where items have the same indent between each other
 * 
 * @param items items which have size and position for distribution
 * @param getItemPosition item position, usually 'x', 'y', 'left' or 'top'
 * @param getItemSize item size, usually 'width' or 'height'
 */
export function distribute<T>(
  items: T[],
  getItemPosition: (item: T) => number,
  getItemSize: (item: T) => number
): Array<{ item: T, newPosition: number }> {
  const occupiedSize = items.map(getItemSize).reduce((prev, current) => prev + current);
  const sortedItems = items.sort((a, b) => getItemPosition(a) - getItemPosition(b));
  const bounds = getBounds(items, getItemPosition, getItemSize);
  const availableBetweenSpace = Math.round((bounds.size - occupiedSize) / (items.length - 1));
  const betweenSpace = Math.max(24, availableBetweenSpace);
  let position = bounds.position;

  return sortedItems.map(item => {
    const resultItem = { item, newPosition: position };
    position += getItemSize(item) + betweenSpace;

    return resultItem;
  });
}

export function getCenter(rect: IRect): IPoint {
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  }
}

/**
 * Returns bounds for single axis
 */
function getBounds<T>(
  items: T[],
  getItemPosition: (item: T) => number,
  getItemSize: (item: T) => number
) {
  const leftBorder = Math.min(...items.map(getItemPosition));
  const rightBorder = Math.max(...items.map(input => getItemPosition(input) + getItemSize(input)));

  return {
    position: leftBorder,
    size: rightBorder - leftBorder,
  };
}
